/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import 'flatpickr/dist/flatpickr.css'
import {KTCard, KTCardBody, Earning, Select, Loading} from '../../../components'
import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {userCreate} from '../../../redux/slices/user.slice'
import {
  allProjectLookup,
  allRoleLookup,
  dynamicReportRoleLookup,
} from '../../../redux/slices/lookup.slice'

import {
  useMemoizedPageUrl,
  useMemoizedFormValues,
  useMemoizedValidationSchema,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  roleLookup: state.lookup.roleLookup,
  allProjectLookupList: state.lookup.allProjectLookupList,
  dynamicReportRoleLookupList: state.lookup.dynamicReportRoleLookupList
})

const selectUserState = (state) => ({
  isRedirect: state.user.isRedirect,
  loading: state.user.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)
  
  const {roleLookup, allProjectLookupList, dynamicReportRoleLookupList} = useSelector(selectLookupData)
  const {isRedirect, loading} = useSelector(selectUserState)
  const dispatch = useDispatch()

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    FirstName: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    LastName: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    Email: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  useEffect(() => {
    dispatch(allProjectLookup())
    dispatch(allRoleLookup({}))
    dispatch(dynamicReportRoleLookup({}))
  }, [dispatch])

  const formValues = useMemoizedFormValues({
    FirstName: '',
    LastName: '',
    Email: '',
    Roles: [],
    ProjectIds: [],
    DynamicReportIds: [],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(userCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.USERS'}),
      path: '/admin/users',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  const handleRedirect = useCallback(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  useEffect(() => {
    handleRedirect()
  }, [handleRedirect])

  const pageTitle = intl.formatMessage({id: 'BC.USERS.ADD'})

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{pageTitle}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USER.CREATE.FIRSTNAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('FirstName')}
                    />
                    {formik.touched.FirstName && formik.errors.FirstName && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.FirstName}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USER.CREATE.LASTNAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('LastName')}
                    />
                    {formik.touched.LastName && formik.errors.LastName && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.LastName}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USER.CREATE.EMAIL'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Email')}
                    />
                    {formik.touched.Email && formik.errors.Email && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.Email}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'USER.CREATE.ROLES'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={roleLookup}
                      {...formik.getFieldProps('Roles')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        if (item) {
                          item.map((data) => newData.push(data.value))
                        }
                        formik.setFieldValue('Roles', newData)
                      }}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USER.CREATE.DYNAMICREPORTROLES'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={dynamicReportRoleLookupList}
                      {...formik.getFieldProps('DynamicReportIds')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        if (item) {
                          item.map((data) => newData.push(data.value))
                        }
                        formik.setFieldValue('DynamicReportIds', newData)
                      }}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USER.CREATE.PROJECTS'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={allProjectLookupList}
                      required
                      {...formik.getFieldProps('ProjectIds')}
                      isMulti
                      closeMenuOnSelect={false}
                      onChange={(item) => {
                        const newData = []
                        if (item) {
                          item.map((data) => newData.push(data.value))
                        }
                        formik.setFieldValue('ProjectIds', newData)
                      }}
                    />
                  </div>
                </div>
                <div className='row pt-15'>
                  <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      className='btn btn-light'
                      onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                    >
                      {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>

        <div className='col-xl-3'>
          {formik.values.StaffId && <Earning StaffId={formik.values.StaffId} />}
        </div>
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Create}
